"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.CoreServicesAPI = void 0;
const socket_io_client_1 = __importDefault(require("socket.io-client"));
const CONVERSATIONS_PATH_SEGMENT = "conversations";
const EMAIL_PATH_SEGMENT = "emailclient";
const QUEUE_PATH_SEGMENT = "store-queue";
class CoreServicesAPI {
    constructor(config, request, getAccessToken) {
        this.config = config;
        this.request = request;
        this.getAccessToken = getAccessToken;
        this._accessToken = null;
    }
    get accessToken() {
        return this._accessToken || (this.getAccessToken && this.getAccessToken());
    }
    set accessToken(token) {
        this._accessToken = token;
    }
    get baseTenantUrl() {
        return `https://${this.config.tenant}.${this.config.domain}/api/v1/${this.config.application || "global"}`;
    }
    get baseEnvironmentUrl() {
        return `${this.baseTenantUrl}/${this.config.environment}`;
    }
    _createQueryString(params) {
        if (params) {
            const cleanedParams = Object.entries(params).reduce((acc, [key, value]) => {
                if (value !== null &&
                    value !== undefined &&
                    (typeof value !== "number" || !isNaN(value))) {
                    acc[key] = value;
                }
                return acc;
            }, {});
            return new URLSearchParams(cleanedParams).toString();
        }
        return "";
    }
    _sendRequest(options) {
        const headers = {};
        const token = this.accessToken;
        if (token) {
            headers["Authorization"] = `Bearer ${token}`;
        }
        if (this.config.enableXTrace) {
            headers["X-Mad-xTrace"] = "true";
        }
        if (this.config.canaryTag) {
            headers["canary-tag"] = this.config.canaryTag;
        }
        return this.request(Object.assign(Object.assign({}, options), { headers }));
    }
    callEndpoint(options) {
        const baseRequestData = this.config.auxillaryData
            ? { aux: this.config.auxillaryData }
            : {};
        return this._sendRequest({
            data: options.data
                ? Object.assign(Object.assign({}, baseRequestData), options.data) : baseRequestData,
            method: options.type === "Query" ? "GET" : "POST",
            url: `${this.baseEnvironmentUrl}/${options.endpointName}`,
        });
    }
    //#region Analytics
    sendAnalyticsEvent(applicationName, event, eventName) {
        return this._sendRequest({
            data: this.config.auxillaryData
                ? Object.assign(Object.assign({}, event), { aux: this.config.auxillaryData }) : event,
            method: "POST",
            url: `https://${this.config.tenant}.${this.config.domain}/api/analytics/${applicationName}/${this.config.environment}/${eventName || ""}`,
        });
    }
    //#endregion Analytics
    //#region Auth
    fetchApplicationToken(providerName, idToken) {
        return this._sendRequest({
            data: {
                access_token: idToken,
                scope: `${this.config.application || "global"}:${this.config.environment}`,
            },
            method: "POST",
            url: `https://auth.${this.config.domain}/api/v2/app-auth/${this.config.tenant}/providers/${providerName}/token`,
        });
    }
    fetchPlatformToken(apiKey) {
        return this._sendRequest({
            data: {
                key: apiKey,
            },
            method: "POST",
            url: `https://${this.config.tenant}.${this.config.domain}/api/v1/auth/token`,
        });
    }
    //#endregion Auth
    //#region Config
    fetchV2Configuration(appConfigName) {
        return this._sendRequest({
            method: "GET",
            url: `https://${this.config.tenant}.${this.config.domain}/api/v2/composer/${this.config.environment}/${appConfigName}/application/configuration`,
        });
    }
    fetchV3Configuration(payload) {
        return this._sendRequest({
            data: payload,
            method: "POST",
            url: `https://${this.config.tenant}.${this.config.domain}/api/v3/composer/${this.config.environment}/${this.config.application || "global"}/application/configuration`,
        });
    }
    //#endregion
    //#region Conversations
    fetchConversations(queryStringParams) {
        return this._sendRequest({
            method: "GET",
            url: `${this.baseEnvironmentUrl}/${CONVERSATIONS_PATH_SEGMENT}?${this._createQueryString(queryStringParams)}`,
        });
    }
    sendCreateSMSConversation(payload) {
        return this._sendRequest({
            data: payload,
            method: "POST",
            url: `${this.baseEnvironmentUrl}/${CONVERSATIONS_PATH_SEGMENT}`,
        });
    }
    fetchConversationsMessages(conversationId, queryStringParams) {
        return this._sendRequest({
            method: "GET",
            url: `${this.baseEnvironmentUrl}/${CONVERSATIONS_PATH_SEGMENT}/${conversationId}/messages?${this._createQueryString(queryStringParams)}`,
        });
    }
    fetchConversationsInfo(queryStringParams) {
        return this._sendRequest({
            method: "GET",
            url: `${this.baseEnvironmentUrl}/${CONVERSATIONS_PATH_SEGMENT}/info?${this._createQueryString(queryStringParams)}`,
        });
    }
    fetchConversationMetadata(conversationId, storeId) {
        return this._sendRequest({
            method: "GET",
            url: `${this.baseEnvironmentUrl}/${CONVERSATIONS_PATH_SEGMENT}/${conversationId}?${this._createQueryString({ storeId })}`,
        });
    }
    sendMarkConversationRead(conversationId) {
        return this._sendRequest({
            method: "POST",
            url: `${this.baseEnvironmentUrl}/${CONVERSATIONS_PATH_SEGMENT}/${conversationId}/read`,
        });
    }
    sendMarkConversationUnread(conversationId) {
        return this._sendRequest({
            method: "POST",
            url: `${this.baseEnvironmentUrl}/${CONVERSATIONS_PATH_SEGMENT}/${conversationId}/unread`,
        });
    }
    sendAssignCustomerToConversation(conversationId, customerData, group, storeId) {
        return this._sendRequest({
            data: customerData,
            method: "PUT",
            url: `${this.baseEnvironmentUrl}/${CONVERSATIONS_PATH_SEGMENT}/${conversationId}?group=${group}&storeId=${storeId}`,
        });
    }
    sendConversationMessage(conversationId, data) {
        return this._sendRequest({
            data,
            method: "POST",
            url: `${this.baseEnvironmentUrl}/${CONVERSATIONS_PATH_SEGMENT}/${conversationId}/messages`,
        });
    }
    fetchPhoneInfo(transportName, phoneNumber) {
        return this._sendRequest({
            method: "GET",
            url: `${this.baseEnvironmentUrl}/${CONVERSATIONS_PATH_SEGMENT}/${transportName}/phone-info?phone=${phoneNumber}`,
        });
    }
    sendUploadConversationFile(name, 
    /**
     * MIME type
     */
    type) {
        return this._sendRequest({
            data: {
                name,
                type,
            },
            method: "POST",
            url: `${this.baseTenantUrl}/${CONVERSATIONS_PATH_SEGMENT}/files`,
        });
    }
    //#endregion Conversations
    //#region Email
    sendOutlook365SubscriptionRequest(transportName, data) {
        return this._sendRequest({
            data,
            method: "POST",
            url: `${this.baseEnvironmentUrl}/${EMAIL_PATH_SEGMENT}/${transportName}/o365-subscription`,
        });
    }
    sendEWSSubscriptionRequest(transportName, data) {
        return this._sendRequest({
            data,
            method: "POST",
            url: `${this.baseEnvironmentUrl}/${EMAIL_PATH_SEGMENT}/${transportName}/ews-subscription`,
        });
    }
    fetchEmailFolders(transportName, sharedAccount) {
        const url = `${this.baseEnvironmentUrl}/${EMAIL_PATH_SEGMENT}/${transportName}/folders`;
        return this._sendRequest({
            method: "GET",
            url: sharedAccount
                ? `${url}?${this._createQueryString({ sharedAccount })}`
                : url,
        });
    }
    fetchEmailDetails(transportName, emailId, sharedAccount) {
        const url = `${this.baseEnvironmentUrl}/${EMAIL_PATH_SEGMENT}/${transportName}/messages/${emailId}`;
        return this._sendRequest({
            method: "GET",
            url: sharedAccount
                ? `${url}?${this._createQueryString({ sharedAccount })}`
                : url,
        });
    }
    sendMarkEmailRead(transportName, emailId, isRead, sharedAccount) {
        const url = `${this.baseEnvironmentUrl}/${EMAIL_PATH_SEGMENT}/${transportName}/messages/${emailId}`;
        return this._sendRequest({
            data: {
                isRead,
            },
            method: "PATCH",
            url: sharedAccount
                ? `${url}?${this._createQueryString({ sharedAccount })}`
                : url,
        });
    }
    fetchEmailThread(transportName, params, sharedAccount) {
        return this._sendRequest({
            method: "GET",
            url: `${this.baseEnvironmentUrl}/${EMAIL_PATH_SEGMENT}/${transportName}/messages?${this._createQueryString(Object.assign(Object.assign({}, params), { sharedAccount }))}`,
        });
    }
    sendDeleteEmail(transportName, emailId, sharedAccount) {
        const url = `${this.baseEnvironmentUrl}/${EMAIL_PATH_SEGMENT}/${transportName}/messages/${emailId}`;
        return this._sendRequest({
            method: "DELETE",
            url: sharedAccount
                ? `${url}?${this._createQueryString({ sharedAccount })}`
                : url,
        });
    }
    sendNewEmailMessage(transportName, data, sharedAccount) {
        const url = `${this.baseEnvironmentUrl}/${EMAIL_PATH_SEGMENT}/${transportName}/messages`;
        return this._sendRequest({
            data,
            method: "POST",
            url: sharedAccount
                ? `${url}?${this._createQueryString({ sharedAccount })}`
                : url,
        });
    }
    sendBatchEmailMessage(transportName, data, sharedAccount) {
        const url = `${this.baseEnvironmentUrl}/${EMAIL_PATH_SEGMENT}/${transportName}/batch-messages`;
        return this._sendRequest({
            data,
            method: "POST",
            url: sharedAccount
                ? `${url}?${this._createQueryString({ sharedAccount })}`
                : url,
        });
    }
    sendReplyToEmail(transportName, emailId, message, sharedAccount) {
        const url = `${this.baseEnvironmentUrl}/${EMAIL_PATH_SEGMENT}/${transportName}/messages/${emailId}/reply`;
        return this._sendRequest({
            data: message,
            method: "POST",
            url: sharedAccount
                ? `${url}?${this._createQueryString({ sharedAccount })}`
                : url,
        });
    }
    sendUploadEmailFile(name, 
    /**
     * MIME type
     */
    type) {
        return this._sendRequest({
            data: {
                name,
                type,
            },
            method: "POST",
            url: `${this.baseTenantUrl}/${EMAIL_PATH_SEGMENT}/files`,
        });
    }
    //#endregion Email
    //#region Queue
    sendUpdateQueueItem(id, item) {
        return this._sendRequest({
            data: item,
            method: "PUT",
            url: `${this.baseEnvironmentUrl}/${QUEUE_PATH_SEGMENT}/${id}`,
        });
    }
    sendCreateQueueItem(item) {
        return this._sendRequest({
            data: item,
            method: "POST",
            url: `${this.baseEnvironmentUrl}/${QUEUE_PATH_SEGMENT}`,
        });
    }
    sendDeleteQueueItem(id) {
        return this._sendRequest({
            method: "DELETE",
            url: `${this.baseEnvironmentUrl}/${QUEUE_PATH_SEGMENT}/${id}`,
        });
    }
    fetchQueueItems(storeId, queueName) {
        return this._sendRequest({
            method: "GET",
            url: `${this.baseEnvironmentUrl}/${QUEUE_PATH_SEGMENT}?${this._createQueryString({
                queueName,
                storeId,
            })}`,
        });
    }
    //#endregion Queue
    //#region Push Notifications
    sendCreateDeviceNotificationSubscription(transportName, payload) {
        return this._sendRequest({
            data: Object.assign(Object.assign({}, payload), { type: "device" }),
            method: "POST",
            url: `${this.baseEnvironmentUrl}/notifications/${transportName}/subscription`,
        });
    }
    //#endregion
    //#region WebSockets
    /**
     *
     * @param query
     * @param options https://socket.io/docs/v2/client-api/#new-Manager-url-options
     * @returns A v2 Socket.IO instance https://socket.io/docs/v2
     */
    createSocketConnection(query, options = {}) {
        return socket_io_client_1.default(`https://${this.config.tenant}.${this.config.domain}`, Object.assign(Object.assign({}, options), { path: "/api/ws", query: Object.assign(Object.assign({}, query), { groups: query.groups ? query.groups.join(",") : undefined, token: query.publicUser ? undefined : query.token || this.accessToken }), transports: ["websocket"] }));
    }
}
exports.CoreServicesAPI = CoreServicesAPI;
//# sourceMappingURL=CoreServicesAPI.js.map